import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Product, ProductService } from 'gung-standard';
import { forkJoin, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs';
import { HlDisplayPurchasedProduct } from '../models/purchased-product';
import { HlDisplayMetadataService } from './hl-display-metadata.service';
import { FilterSettingsGroup, HlDisplayPortalFeaturesService } from './hl-display-portal-features.service';
import { HlDisplayHelperService } from './hl-display-helper.service';

interface PurchasedProduct {
  artnr: string;
  latestDeliveryDate: number;
  total: number;
  product: Product;
}

export const langExists =
  (lang: string | undefined) =>
    (data: Product): boolean =>
      lang && data.extra.i18n && data.extra.i18n[lang];

@Injectable({
  providedIn: 'root'
})
export class HlProductService extends ProductService {
  private translate = inject(TranslateService);
  private metadataService = inject(HlDisplayMetadataService);
  private helperService = inject(HlDisplayHelperService);
  private hlDisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);

  public getProduct(productId: string): Observable<Product> {
    return super.getProduct(productId).pipe(map(product => this.transformProduct(product)));
  }

  public getProductsByIds(productIds: string[]): Observable<Product[]> {
    return super
      .getProductsByIds(productIds)
      .pipe(map(products => products.map(product => this.transformProduct(product))));
  }

  public getProductsByAssortment(assortmentId: string): Observable<Product[]> {
    return super
      .getProductsByAssortment(assortmentId)
      .pipe(map(products => products.map(product => this.transformProduct(product))));
  }

  public getComponentsByIds(productIds: string[]): Observable<Product[]> {
    const url = `json/hl-display/components`;

    return this.http.post<Product[]>(url, productIds);
  }

  public getCustomerPurchasedProducts(): Observable<HlDisplayPurchasedProduct[]> {
    return forkJoin([
      this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first()),
      this.authService.getRoles().pipe(first()),
      this.authService.getCurrentUser().pipe(first()),
      this.hlDisplayPortalFeaturesService.getPortalFeatureGroup('filterSettingsGroup').pipe(first())
    ]).pipe(
      switchMap(([selectedCustomer, flow, userRoles, currentUser, filterSettingsGroup]) => {
        const filterSettings = filterSettingsGroup as FilterSettingsGroup;
        // note: I know this is not exactly the same as in the product list, but this page is only visible for users with SALES role so not that critical
        const assortmentId$ =
          userRoles.includes('SALES') || filterSettings.hasProductBeenPurchasedByCustomerFilterEnabled
            ? this.helperService.checkRequestPricelistAssortmentWithWebCondition(selectedCustomer.extra.kus.prislista)
            : of(currentUser.assortment as string);
        return assortmentId$.pipe(
          switchMap(assortmentId => {
            const url = `json/hl-display/customer-purchased-before/${assortmentId}/${selectedCustomer.id}/${flow.id}`;
            return this.http.get<PurchasedProduct[]>(url);
          })
        );
      }),
      map(customerPurchasedProducts => {
        return customerPurchasedProducts.map(customerPurchasedProduct => {
          const { product, ...allPropertiesButProduct } = customerPurchasedProduct;
          return {
            ...this.transformProduct(customerPurchasedProduct.product),
            ...allPropertiesButProduct
          };
        });
      })
    );
  }

  private transformProduct(productToMap: Product): Product {
    const languageCode = this.translate.currentLang;
    const product: Product = {
      ...productToMap,
      extra: {
        ...productToMap.extra,
        hlPimProduct: {
          ...productToMap.extra.hlPimProduct
        },
        hlPimItem: {
          ...productToMap.extra.hlPimItem
        },
        minimumOrderQuantity: 1,
        stepAmount: 1,
        i18n: {
          ...this.mergeI18n(productToMap.extra.i18n, this.metadataService.getMetadataI18n())
        }
      }
    };
    product.extra.minimumOrderQuantity = 1;
    product.extra.stepAmount = 1;
    try {
      product.extra.minimumOrderQuantity = parseInt(product.extra.ar.q_hl_sales_moq, 10);
    } catch (error) {
      console.error(error);
    }
    try {
      const salesQuantity = parseInt(product.extra.ar.q_hl_sales_qty, 10);
      product.extra.stepAmount = salesQuantity;
      product.extra.salesQuantity = salesQuantity;
    } catch (error) {
      console.error(error);
    }

    if (languageCode === product.extra._currentLanguage) {
      return product;
    }

    if (!!product.extra.images) {
      product.extra.images.map(img => {
        if (img.description === 'No Image') {
          img.s3Uri = `hldisplay/images/${languageCode}-no-image.jpg`;
        }
      });
    }

    // product.name = product.extra.ar.artbeskr2 || product.name;
    product.description = product.extra.ar.itemspecweb || product.description || '';
    if (!product.extra.i18n) {
      return product;
    }
    const i18n: { [language: string]: any } = product.extra.i18n;

    if (i18n[languageCode]) {
      const languageMap = i18n[languageCode];

      product.name = languageMap.externalProductName || product.name;

      product.description = languageMap.itemspecweb || '';

      // I believe that all code below here is legacy, but I have removed them one by one to be sure.
      // try to remove this code and just use the filters with the existing data on the products. I think the below
      // code is about 5 years old and is completely unnecessary. - Adam

      /*if (product.extra.hlPimItem.ItemMerchandiseType) {
        const typeList = product.extra.hlPimItem.ItemMerchandiseType as any[];
        product.extra.hlPimItem.ItemMerchandiseType = typeList.map(type => {
          if (languageMap.MerchandiseType && languageMap.MerchandiseType[type.Id]) {
            const key = Object.keys(type.Value).find(key => {
              // Replace this condition with your specific condition
              return key === languageCode;
            });
            return languageMap.MerchandiseType[type.Value[key]];
          } else {
            return type.Value; // default to english
          }
        });
      }*/

      if (product.extra.hlPimProduct.ProductShelfManufacturer) {
        const typeList = product.extra.hlPimProduct.ProductShelfManufacturer as any[];
        product.extra.hlPimProduct.ProductShelfManufacturer = typeList.map(type => {
          if (languageMap.ShelfManufacturer) {
            return languageMap.ShelfManufacturer[type.Value];
          } else {
            return type.Value;
          }
        });
      }
      /*
      if (product.extra.hlPimItem.ProductESLManufacturer) {
        const typeList = product.extra.hlPimItem.ProductESLManufacturer as any[];
        product.extra.hlPimItem.ProductESLManufacturer = typeList.map(type => {
          if (languageMap.ExtProdSys) {
            return languageMap.ExtProdSys[type.Value];
          } else if (languageMap.ESLManufacturer && languageMap.ESLManufacturer[type.Value]) {
            return languageMap.ESLManufacturer[type.Value];
          } else {
            return type.Value;
          }
        });
      }*/

      if (product.extra.hlPimItem.ItemColourName) {
        let typeList = product.extra.hlPimItem.ItemColourName as string[];
        if (!Array.isArray(product.extra.hlPimItem.ItemColourName)) {
          typeList = [product.extra.hlPimItem.ItemColourName];
        }
        product.extra.hlPimProduct.ItemColourName = typeList.map(type => {
          if (languageMap.ItemColourName) {
            return languageMap.ItemColourName[type];
          } else {
            return type;
          }
        });
      }
      product.extra._currentLanguage = languageCode;
    }

    return product;
  }

  // values from i18nSecondary will be overwritten if duplicate keys exist in i18nPrimary
  private mergeI18n(i18nSecondary: any, i18nPrimary: any): any {
    const res: any = {};

    Object.keys(i18nSecondary).forEach(language => {
      res[language] = { ...i18nSecondary[language] };
    });

    Object.keys(i18nPrimary).forEach(language => {
      res[language] = {
        ...(res[language] || {}),
        ...i18nPrimary[language]
      };
    });

    return res;
  }
}
